import HasID from "./HasID";
import HasAddress from "./HasAddress";
import HasLabel from "./HasLabel";
import { RelationshipCode } from '@/models/PatientPlan';
import TextUtils from "@/util/TextUtils";

export interface Plan extends HasID, HasAddress {
    id: number;
    bin: string;
    pcn: string;
    pbmId: number;
    pbmName?: string;
    surescriptsPayerId: string;
    vendorId: string;
    providerType: number;
    name: string;
    phoneNumber: string;
    active: boolean;
    requireDeliverySignature: boolean;
    offlineCopay?: number | null;
    flags: BillingFlags;
    defaultGroupId: string;
    defaultCardholderId: string;
    defaultPersonCode: string;
    defaultRelationshipCode: RelationshipCode;
    inventoryQuantityAvailable: number;
    inventoryLastUpdate: Date;
    planMessage: string;

}

export enum BillingFlags {
    //None = 0, // Commented the None option out as it wont ever be explicitly selected, and this way it doesn't show on UI
    // Include the Patient Residence Code field in claims for this plan.
    PatientResidenceCode = 1 << 0, // 0001 -- the bitshift is unnecessary, but done for consistency
    // Send Pharmacy Service Type as Community/Retail on claims for this plan.
    PharmacyServiceType = 1 << 1,
    // Use the mail-order NPI number instead of the retail NPI when submitting claims.
    UseMailNPI = 1 << 2,
    // Allow quantities greater than the written quantity to be billed.
    AllowMultipleFills = 1 << 3,
    // Plan is managed through ConnectiveRx; enables ConnectiveRx-specific claim and inventory logic.
    ConnectiveRx = 1 << 4,
    // Use 2/4 for coverage codes instead of 3/8.
    OtherCoverageCodes = 1 << 5,
    // Plan is an Arcutis manufacturer copay card; enables Arcutis-specific copay and inventory logic.
    ArcutisCopay = 1 << 6,
    // Use Monarch cardholder ID logic when building claims for this plan.
    MonarchCardholder = 1 << 7,
    // Treat captured (on-hand) inventory as consignment rather than purchased stock.
    CapturedIsConsignment = 1 << 8,
    // Pass a zero amount for Primary Paid on secondary claims even when the primary paid nothing.
    PassZeroPrimaryPaid = 1 << 9,
    // Send Pharmacy Service Type as Mail Order on claims for this plan.
    MailPharmacyServiceType = 1 << 10,
    // Always include the Other Payer Amount Paid in COB segments, even when the value is zero.
    AlwaysSendOtherPayerAmountPaid = 1 << 11,
    // Always send Other Coverage Codes on claims for this plan, regardless of secondary coverage.
    AlwaysSendOtherCoverageCodes = 1 << 12,
    // Treat a plan-paid amount of zero as a consignment fill (no cost to pharmacy).
    PlanPaidZeroIsConsignment = 1 << 13,
    // Suppress the "No Override" Submission Clarification Code on claims. Enable if this plan rejects claims that include this field.
    SuppressNoOverrideClarificationCode = 1 << 14,
    // Send Place of Service (field 307-C7) as "12" (Home) instead of the default "01" (Pharmacy) on claims for this plan.
    PlaceOfServiceHome = 1 << 15,
    // Always send the Unit of Measure field on claims, even for unit-dose products. Required by some payers (e.g. CO Medicaid) that reject claims without this field.
    AlwaysSendUnitOfMeasure = 1 << 16,
}

export class Plan extends HasLabel {
    constructor(plan?: Plan) {
        super();
        this.defaultRelationshipCode = RelationshipCode.Cardholder;
        if (plan) {
            Object.assign(this, plan);
        }
        if (plan?.active == null || plan?.active == undefined) this.active = true;
        if (plan?.requireDeliverySignature == null || plan?.requireDeliverySignature == undefined) this.requireDeliverySignature = false;
    }

    toString(): string {
        const text = [];

        if (this.name?.trim())
            text.push(this.name.trim());

        if (this.bin?.trim())
            text.push(`BIN: ${this.bin.trim()}`);

        if (this.pcn?.trim())
            text.push(`PCN: ${this.pcn.trim()}`);

        return text.join(", ");
    }

    get selectedFlags(): string[] {
        const ret: string[] = [];
        if (this.flags) {
            Plan.flagOptions.forEach((o) => {
                if (this.flags & (BillingFlags[o.value] as any)) ret.push(o.value);
            })
        }
        return ret;
    }

    set selectedFlags(vals: string[]) {
        this.flags = 0;
        vals.forEach((flag: any) => {
            const flagVal: any = BillingFlags[flag];
            this.flags |= flagVal;
        });
    }

    get selectedFlagsEnums(): Array<BillingFlags> {
        return this.selectedFlags?.map<BillingFlags>((f: any) => (BillingFlags[f] as any));
    }

    get doesAllowsMultifill(): boolean {
        return this.selectedFlagsEnums?.some(f => f == BillingFlags.AllowMultipleFills);
    }

    static get flagOptions(): Array<{ text: string; value: string; description: string }> {
        const info: Partial<Record<keyof typeof BillingFlags, { text: string; description: string }>> = {
            PatientResidenceCode: { text: 'Patient Residence Code', description: 'Include the patient residence code field in claims if the patient has a residence code on file.' },
            PharmacyServiceType: { text: 'Pharmacy Service Type (Retail)', description: 'Set pharmacy service type to Community/Retail on claims for this plan.' },
            UseMailNPI: { text: 'Use Mail NPI', description: 'Use the mail-order NPI instead of the retail NPI on claims when the prescription day supply meets the configured threshold.' },
            AllowMultipleFills: { text: 'Allow Multiple Fills', description: 'Allow quantities greater than the written quantity to be billed for this plan.' },
            ConnectiveRx: { text: 'ConnectiveRx', description: 'Trigger a ConnectiveRx API enrollment when this plan is assigned to a patient, automatically populating the group and member IDs.' },
            OtherCoverageCodes: { text: 'Other Coverage Codes', description: 'Use coverage codes 2/4 instead of 3/8 when submitting COB segments for this plan.' },
            ArcutisCopay: { text: 'Arcutis Copay', description: 'Trigger an Arcutis copay card enrollment when this plan is assigned to a patient, populating group and member IDs. Also syncs the patient\'s email with Arcutis on updates.' },
            MonarchCardholder: { text: 'Monarch Cardholder', description: 'On secondary claims, use the primary plan\'s Group ID as cardholder first name and the primary plan\'s PCN as cardholder last name, instead of the patient\'s actual name.' },
            CapturedIsConsignment: { text: 'Captured Is Consignment', description: 'When a claim returns a "Captured" transaction status, mark the prescription as consignment with zero cost to the pharmacy.' },
            PassZeroPrimaryPaid: { text: 'Pass Zero Primary Paid', description: 'In COB segments for secondary claims, explicitly send zero as the primary paid amount and set the Other Coverage Code to indicate payment was applied.' },
            MailPharmacyServiceType: { text: 'Pharmacy Service Type (Mail)', description: 'Set pharmacy service type to Mail Order on claims for this plan.' },
            AlwaysSendOtherPayerAmountPaid: { text: 'Always Send Other Payer Amount Paid', description: 'Always include the Other Payer Amount Paid in COB segments, even when zero. Also prevents this amount from being used as a voucher value.' },
            AlwaysSendOtherCoverageCodes: { text: 'Always Send Other Coverage Codes', description: 'Always include the Other Coverage Code field in the claim, even when no secondary coverage is present (i.e. on primary-only claims).' },
            PlanPaidZeroIsConsignment: { text: 'Plan Paid Zero Is Consignment', description: 'When the plan-paid amount on a claim is zero, mark the prescription as consignment with zero cost to the pharmacy.' },
            SuppressNoOverrideClarificationCode: { text: 'Suppress No-Override Clarification Code', description: 'Do not send the "No Override" submission clarification code. Enable this for plans that reject claims containing this field.' },
            PlaceOfServiceHome: { text: 'Place of Service - Home', description: 'Send Place of Service (field 307-C7) as "12" (Home) instead of the default "01" (Pharmacy) on claims for this plan. Required by payers such as NC Medicaid Direct that reject the pharmacy place-of-service code.' },
            AlwaysSendUnitOfMeasure: { text: 'Always Send Unit of Measure', description: 'Always include the Unit of Measure field (NCPDP field 600-28) on claims, even for unit-dose products. Required by payers such as CO Medicaid that reject claims missing this field (reject codes 26/PZ).' },
        };
        return Object.keys(BillingFlags)
            .filter(val => !isNaN(Number(val)))
            .map(k => {
                const name = BillingFlags[Number(k)] as keyof typeof BillingFlags;
                const entry = info[name];
                return { text: entry?.text ?? name, value: name, description: entry?.description ?? '' };
            });
    }

    get isOffline(): boolean {
        return !isNaN(((this.offlineCopay === null ? undefined : this.offlineCopay) as any));
    }

    get providerTypeDesc(): string {
        return Plan.ProviderTypeToString(this.providerType);
    }

    /**
     * Bootstrap badge variant used for the plan-type pill. Mirrors the colors
     * used for the eligibility result badges so Medicare/Medicaid read the same
     * across the patient insurance screens.
     */
    get providerTypePillVariant(): string {
        switch (this.providerType) {
            case Plan.ProviderType.Medicare:
                return "primary";
            case Plan.ProviderType.Medicaid:
                return "info";
            default:
                return "secondary";
        }
    }

    /**
     * Plan-type pill markup appended to the search-result label. Returns an
     * empty string for Unknown/unset types so we don't render a meaningless
     * pill. Rendered via v-html in SearchComponent's option template.
     */
    get providerTypePillHtml(): string {
        const desc = this.providerTypeDesc;
        if (!desc || this.providerType === Plan.ProviderType.Unknown) return "";
        return `<span class="badge badge-${this.providerTypePillVariant} ml-1">${desc}</span>`;
    }

    /**
     * PBM pill markup appended to the search-result label so staff can tell
     * which Pharmacy Benefit Manager a plan belongs to (plans are searchable
     * by PBM name). Returns an empty string when the plan has no PBM. The name
     * is HTML-escaped because it's rendered via v-html.
     */
    get pbmPillHtml(): string {
        const name = this.pbmName?.trim();
        if (!name) return "";
        const safe = name.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        return `<span class="badge badge-light ml-1">${safe}</span>`;
    }

    /**
     * Plan search results render the label through v-html, so expose the
     * plan-type and PBM pills via the label suffix hook. getLabel()/toString()
     * stay plain text for the many escaped-text callers.
     */
    getLabelSuffixHtml(): string {
        return `${this.providerTypePillHtml}${this.pbmPillHtml}`;
    }

    public static ProviderTypeToString(num: Plan.ProviderType): string {
        if (isNaN(num) || num == null) return '';
        let res = Plan.ProviderType[num];
        res = TextUtils.camelCaseToNormal(res);

        return res;
    }
}

export namespace Plan {
    export enum ProviderType {
        Unknown, Medicare, Medicaid, Commercial, Coupon, TriCare, ChampVa
        , Government, WorkmanComp, Medical,
    }
}
